import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MerchantApplicationProvider } from "../../context/MerchantApplicationContext";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import NereaChat from "./components/NereaChat";
import ImpersonationBanner from "../../components/admin/ImpersonationBanner";
import { impersonationStore } from "../../lib/impersonation";

const SECTION_TITLES = {
  "/app": "Dashboard",
  "/app/dashboard": "Dashboard",
  "/app/onboarding": "Onboarding",
  "/app/financials": "Financials",
  "/app/gateway": "Gateway",
  "/app/integration": "Integration",
  "/app/settings": "Settings",
};

/**
 * Authenticated merchant app shell — Brief 2 §1.
 * Wraps every back-office page in a MerchantApplicationProvider so the status
 * pill, ref number, and progress data are available everywhere without
 * re-fetching.
 */
export default function AppShell() {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // Close the mobile drawer when route changes.
  useEffect(() => { setSidebarOpen(false); }, [pathname]);

  const title = useMemo(() => {
    // Match exact then prefix; default to "Dashboard".
    if (SECTION_TITLES[pathname]) return SECTION_TITLES[pathname];
    const match = Object.keys(SECTION_TITLES).find(
      (k) => k !== "/app" && pathname.startsWith(k)
    );
    return match ? SECTION_TITLES[match] : "Dashboard";
  }, [pathname]);

  return (
    <MerchantApplicationProvider>
      <ImpersonationBanner />
      <div
        data-testid="app-shell"
        className={`min-h-screen bg-[#0a0a0a] text-white font-body relative overflow-hidden ${
          impersonationStore.isActive() ? "pt-[58px]" : ""
        }`}
      >
        {/* Ambient glow — calmer than the public site so charts stay readable. */}
        <div className="pointer-events-none absolute -top-32 left-[20%] h-[480px] w-[700px] rounded-full bg-[#0055FF]/[0.10] blur-[140px]" />
        <div className="pointer-events-none absolute bottom-[-20%] right-[-5%] h-[400px] w-[400px] rounded-full bg-[#00E5FF]/[0.06] blur-[140px]" />

        <div className="relative flex">
          {/* Desktop sidebar */}
          <aside
            data-testid="app-sidebar-desktop"
            className="hidden md:flex flex-col w-64 shrink-0 min-h-screen border-r border-white/[0.06] bg-black/20 backdrop-blur-xl"
          >
            <Sidebar onOpenSupport={() => setSupportOpen(true)} />
          </aside>

          {/* Mobile drawer */}
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                key="drawer"
                data-testid="app-sidebar-drawer"
                className="md:hidden fixed inset-0 z-40 flex"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div
                  className="absolute inset-0 bg-black/70 backdrop-blur-md"
                  onClick={() => setSidebarOpen(false)}
                />
                <motion.aside
                  initial={{ x: -300 }}
                  animate={{ x: 0 }}
                  exit={{ x: -320 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="relative flex flex-col w-72 max-w-[85%] min-h-screen border-r border-white/[0.06] bg-[#0a0a0a]"
                >
                  <Sidebar
                    onClose={() => setSidebarOpen(false)}
                    onOpenSupport={() => setSupportOpen(true)}
                  />
                </motion.aside>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main column */}
          <main className="flex-1 min-w-0 min-h-screen flex flex-col">
            <TopBar
              title={title}
              onOpenSupport={() => setSupportOpen(true)}
              onOpenSidebar={() => setSidebarOpen(true)}
            />
            <div className="flex-1 px-5 sm:px-8 py-8 sm:py-10">
              <Outlet />
            </div>
          </main>
        </div>

        <NereaChat
          open={supportOpen}
          onOpen={() => setSupportOpen(true)}
          onClose={() => setSupportOpen(false)}
        />
      </div>
    </MerchantApplicationProvider>
  );
}
